import React, {createContext, useContext, useMemo, useRef, ReactNode} from 'react';

interface EventsListContextValue {
    /** Unique identifier for this list instance */
    listId: string;
    /** Reference to the scrollable container element */
    containerRef: React.RefObject<HTMLDivElement | null>;
}

const EventsListContext = createContext<EventsListContextValue | null>(null);

interface EventsListProviderProps {
    children: ReactNode;
    listId?: string;
}

/**
 * Provider for events list context
 * Provides a unique list identifier and container reference to all child components
 */
export function EventsListProvider({children, listId}: EventsListProviderProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    // Generate a unique ID if not provided
    const uniqueListId = useMemo(() => {
        return listId || `events-list-${Math.random().toString(36).substring(2, 9)}`;
    }, [listId]);

    const value = useMemo<EventsListContextValue>(
        () => ({
            listId: uniqueListId,
            containerRef,
        }),
        [uniqueListId]
    );

    return (
        <EventsListContext.Provider value={value}>
            {children}
        </EventsListContext.Provider>
    );
}

/**
 * Hook to access the events list context
 * Provides the list ID and container reference for scroll management
 */
export function useEventsListContext() {
    const context = useContext(EventsListContext);

    if (!context) {
        throw new Error('useEventsListContext must be used within EventsListProvider');
    }

    return context;
}
